<template>
  <div class="crud_form_wrapper provider_show_page" :dir="pageDir">
    <div class="form_title_wrapper">
      <h4>{{ $t("MESSAGES.Show festival provider") }}</h4>
    </div>

    <div class="col-12 text-end mb-3">
      <v-btn @click="$router.go(-1)" class="provider_show_back_btn" icon>
        <i
          :class="$i18n.locale === 'en' ? 'fas fa-forward' : 'fas fa-backward'"
        ></i>
      </v-btn>
    </div>

    <div v-if="loading" class="provider_show_loading">
      <v-progress-circular indeterminate :color="themeAccentColor" />
    </div>

    <div v-else class="single_step_form_content_wrapper provider_show_content">
      <provider-show-header
        :logo="data.media?.path"
        :name-ar="data.nameAr"
        :name-en="data.nameEn"
        :is-active="data.active"
        @preview="openImage"
      />

      <provider-show-details
        :description-ar="data.descProd"
        :description-en="data.descProdEn"
        :email="data.email"
        :mobile="data.mobile"
        :commission="data.commission"
      />

      <provider-show-owner
        :owner-name="data.owner_name"
        :owner-image="data.owner_media?.path"
        @preview="openImage"
      />

      <provider-show-contacts
        :phones="contactPhones"
        :whatsapp="data.whatsapp_number"
      />

      <provider-show-social :links="data.social_media_links" />

      <provider-show-tables
        ref="providerTables"
        :staff="staff"
        :transactions="transactions"
        :action-loading-id="staffActionLoadingId"
        :accent-color="themeAccentColor"
        :active-tab.sync="activeTab"
        @preview="openImage"
        @activate-staff="toggleStaffStatus"
        @deactivate-staff="onDeactivateStaff"
      />
    </div>

    <image-modal
      v-if="dialogImage"
      :type="selectedItemType"
      :modalIsOpen="dialogImage"
      :modalImage="selectedItemImage"
      @toggleModal="dialogImage = !dialogImage"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ProviderShowHeader from "@/components/festivalProviders/show/ProviderShowHeader.vue";
import ProviderShowDetails from "@/components/festivalProviders/show/ProviderShowDetails.vue";
import ProviderShowOwner from "@/components/festivalProviders/show/ProviderShowOwner.vue";
import ProviderShowContacts from "@/components/festivalProviders/show/ProviderShowContacts.vue";
import ProviderShowSocial from "@/components/festivalProviders/show/ProviderShowSocial.vue";
import ProviderShowTables from "@/components/festivalProviders/show/ProviderShowTables.vue";

export default {
  name: "ShowFestivalProvider",

  components: {
    ProviderShowHeader,
    ProviderShowDetails,
    ProviderShowOwner,
    ProviderShowContacts,
    ProviderShowSocial,
    ProviderShowTables,
  },

  computed: {
    ...mapGetters({
      getAppTheme: "AppThemeModule/getAppTheme",
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    pageDir() {
      return this.getAppLocale === "ar" ? "rtl" : "ltr";
    },

    themeAccentColor() {
      return this.getAppTheme === "dark_theme" ? "#6f8f84" : "#405950";
    },

    contactPhones() {
      return (this.phone_numbers || [])
        .map((item) => String(item.phone ?? "").trim())
        .filter(Boolean);
    },
  },

  data() {
    return {
      loading: true,
      activeTab: 0,
      staff: [],
      transactions: [],
      staffActionLoadingId: null,
      dialogImage: false,
      selectedItemImage: null,
      selectedItemType: "image",
      phone_numbers: [],
      data: {
        media: { path: null },
        owner_media: { path: null },
        email: null,
        owner_name: null,
        mobile: null,
        commission: null,
        nameAr: null,
        nameEn: null,
        descProd: null,
        descProdEn: null,
        whatsapp_number: null,
        social_media_links: {
          facebook: null,
          instagram: null,
          tiktok: null,
          x: null,
          snapchat: null,
        },
        active: true,
      },
    };
  },

  methods: {
    openImage(path) {
      if (!path) return;
      this.dialogImage = true;
      this.selectedItemImage = path;
      this.selectedItemType = path.endsWith("mp4") ? "video" : "image";
    },

    async toggleStaffStatus(item, deactivationReason = null) {
      this.staffActionLoadingId = item.id;
      const REQUEST_DATA = new FormData();
      if (deactivationReason) {
        REQUEST_DATA.append("deactivation_reason", deactivationReason);
      }

      try {
        await this.$axios({
          method: "POST",
          url: `festival-providers/staff/status/${item.id}`,
          data: REQUEST_DATA,
        });
        this.$message.success(this.$t("MESSAGES.changeActivation"));
        await this.getStoreData(false);
        if (this.$refs.providerTables) {
          this.$refs.providerTables.closeDeactivateModal();
        }
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        this.staffActionLoadingId = null;
      }
    },

    onDeactivateStaff({ item, reason }) {
      this.toggleStaffStatus(item, reason);
    },

    async getStoreData(showLoader = true) {
      if (showLoader) this.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: `festival-providers/${this.$route.params?.id}`,
        });
        const provider = res.data.data.FestivalProvider;

        this.staff = provider.staff || [];
        this.transactions = provider.transactions || [];
        this.data.nameAr = provider.name_ar;
        this.data.nameEn = provider.name_en;
        this.data.descProd = provider.description_ar;
        this.data.descProdEn = provider.description_en;
        this.data.media.path = provider.logo;
        this.data.active = provider.is_active;
        this.data.email = provider.email;
        this.data.owner_name = provider.owner_name;
        this.data.mobile = provider.mobile;
        this.data.commission = provider.commission;
        this.data.whatsapp_number = provider.whatsapp_number;
        this.data.owner_media.path = provider.owner_image;
        this.phone_numbers = (provider.phone_numbers || []).map((phone) => ({
          phone,
        }));
        this.data.social_media_links = provider.social_media_links || {};
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        if (showLoader) this.loading = false;
      }
    },
  },

  created() {
    this.getStoreData();
  },
};
</script>
